<template>
    <div>
        <div v-if="cartIsEmpty" class="py-36 text-gray-700 text-center">
            <p>Your cart is empty.</p>
            <div class="mt-6">
                <a
                    role="button"
                    class="inline-flex justify-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                    href="/product-categories/all-products"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    Browse Our Products
                </a>
            </div>
        </div>

        <div v-else class="mx-6 sm:mx-12 mt-3 sm:mt-0">
            <div class="lg:grid lg:grid-cols-3">
                <div id="cart-items" class="lg:col-span-2">
                    <div
                        class="hidden pl-20 sm:pl-24 pr-4 sm:pr-6 pb-2 sm:grid sm:grid-cols-5"
                    >
                        <div class="col-start-5 text-right">
                            <h3
                                class="inline-block font-semibold text-sm text-nutreance-green"
                            >
                                Price
                            </h3>
                        </div>
                    </div>

                    <div
                        :id="`cart-item-${cartItem.id}`"
                        v-for="cartItem in state.cart.cart_items"
                        :key="cartItem.id"
                        class="bg-white border border-gray-200 shadow rounded-lg mb-4"
                    >
                        <div class="px-4 py-5 sm:p-6 flex">
                            <CartItemImage :cartItem="cartItem" />

                            <div class="ml-3 w-full sm:grid sm:grid-cols-5">
                                <div class="col-span-4">
                                    <p class="font-semibold px-3 pb-1">
                                        <a
                                            class="text-nutreance-green hover:text-nutreance-green-lighter"
                                            :href="
                                                productPageHref(
                                                    cartItem.product_option,
                                                )
                                            "
                                        >
                                            {{
                                                cartItem.product_option.product
                                                    .name
                                            }}
                                            -
                                            {{
                                                cartItem.product_option.product
                                                    .description
                                            }}
                                            ({{
                                                cartItem.product_option
                                                    .description
                                            }})
                                        </a>
                                    </p>

                                    <p
                                        class="sm:hidden px-3 py-2 text-gray-700 text-sm"
                                    >
                                        <span class="font-semibold"
                                            >Price:</span
                                        >
                                        ${{
                                            dollarsForDisplay(
                                                cartItem.product_option.price,
                                            )
                                        }}
                                    </p>

                                    <div
                                        class="px-3 py-2 text-gray-700 text-sm sm:text-base flex items-center"
                                    >
                                        <span class="font-semibold mr-2"
                                            >Qty:</span
                                        >
                                        <CartItemQuantityButtons
                                            :cartItem="cartItem"
                                            :shippingTimeId="
                                                state.shippingTimeId
                                            "
                                        />
                                    </div>

                                    <p
                                        class="px-3 py-2 text-sm text-red-600"
                                        v-if="
                                            errors[cartItem.id] &&
                                            errors[cartItem.id].quantity
                                        "
                                    >
                                        {{ errors[cartItem.id].quantity }}
                                    </p>

                                    <CartItemSubscriptionOptionButtons
                                        v-if="cartItem.subscription_option_id"
                                        class="pl-3 py-2 text-gray-700 text-sm sm:text-base"
                                        :cartItem="cartItem"
                                        :shippingTimeId="state.shippingTimeId"
                                    />

                                    <p
                                        class="px-3 py-2 text-sm text-red-600"
                                        v-if="
                                            errors[cartItem.id] &&
                                            errors[cartItem.id]
                                                .subscription_option_id
                                        "
                                    >
                                        {{
                                            errors[cartItem.id]
                                                .subscription_option_id
                                        }}
                                    </p>

                                    <CartItemDiscounts
                                        :cartItem="cartItem"
                                        :discounts="state.discounts"
                                    />

                                    <CartItemTips
                                        :cartItem="cartItem"
                                        :shippingTimeId="state.shippingTimeId"
                                        :tips="state.tips"
                                    />
                                </div>

                                <div
                                    class="hidden sm:flex sm:flex-col sm:justify-between sm:items-end"
                                >
                                    <span>
                                        ${{
                                            dollarsForDisplay(
                                                cartItem.product_option.price,
                                            )
                                        }}
                                    </span>

                                    <DeleteCartItemButton
                                        :cartItem="cartItem"
                                        :shippingTimeId="state.shippingTimeId"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <CartRecommendedProducts
                        class="md:px-2 py-2"
                        :initialRecommendedProducts="recommendedProducts"
                    />
                </div>

                <div>
                    <div class="md:sticky md:top-2 lg:pl-6 flex flex-col">
                        <div class="order-3 lg:order-1 flex flex-col">
                            <button
                                @click="forgetPaypalNonce"
                                name="checkout"
                                type="button"
                                class="w-full inline-flex justify-center px-6 py-3 my-2 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                            >
                                <span>Proceed to Checkout</span>
                                <ArrowSmRightIcon
                                    class="h-6 w-6 ml-2"
                                    aria-hidden="true"
                                />
                            </button>

                            <div
                                v-if="alternateCheckoutAvailable"
                                class="flex items-center justify-between"
                            >
                                <hr class="border-t border-gray-200 w-full" />
                                <span
                                    class="text-center text-gray-400 text-sm px-8"
                                    >OR</span
                                >
                                <hr class="border-t border-gray-200 w-full" />
                            </div>

                            <div class="my-2 space-y-2">
                                <PaypalCheckoutButton
                                    :amount="state.cartTotals.total"
                                    :errors="errors"
                                    :token="state.paypalToken"
                                />

                                <ApplePayButton
                                    :amount="state.cartTotals.total"
                                />
                            </div>
                        </div>

                        <div
                            class="order-2 lg:order-2 bg-white shadow border border-gray-200 rounded-lg mb-4 lg:mt-4"
                        >
                            <div id="cart-totals" v-if="!cartIsEmpty">
                                <div class="w-full px-4 py-4">
                                    <div
                                        class="mb-4 flex items-baseline justify-between"
                                    >
                                        <dt class="font-semibold text-gray-500">
                                            Subtotal:
                                        </dt>
                                        <dd class="mt-1 text-gray-800">
                                            ${{
                                                dollarsForDisplay(
                                                    state.cartTotals.subtotal,
                                                )
                                            }}
                                        </dd>
                                    </div>

                                    <div
                                        v-if="state.cartTotals.discounts > 0"
                                        class="mb-4 flex items-baseline justify-between"
                                    >
                                        <dt class="font-semibold text-gray-500">
                                            Discounts:
                                        </dt>
                                        <dd class="mt-1 text-gray-800">
                                            - ${{
                                                dollarsForDisplay(
                                                    state.cartTotals.discounts,
                                                )
                                            }}
                                        </dd>
                                    </div>

                                    <ShippingOptionButtons
                                        class="py-1"
                                        @openModal="
                                            shippingRegionModalOpen = true
                                        "
                                        @shippingTimeUpdated="
                                            (shippingTimeId) =>
                                                (state.shippingTimeId =
                                                    shippingTimeId)
                                        "
                                        :country="state.country"
                                        :region="state.region"
                                        :shippingTimeId="state.shippingTimeId"
                                        :shippingOptions="state.shippingOptions"
                                    />

                                    <p
                                        class="px-3 py-1 text-sm text-red-600"
                                        v-if="errors.shipping_option"
                                    >
                                        {{ errors.shipping_option }}
                                    </p>

                                    <div
                                        v-if="state.cartTotals.tax > 0"
                                        class="mt-2 mb-4 flex items-baseline justify-between"
                                    >
                                        <dt class="font-semibold text-gray-500">
                                            Tax
                                            <Tooltip
                                                :message="
                                                    state.country.code === 'US'
                                                        ? 'We collect and remit sales tax according to the rates set by your state and local government.'
                                                        : 'We collect taxes and pay them directly to your local government to prevent unexpected fees or delays.'
                                                "
                                            />
                                            :
                                        </dt>
                                        <dd class="mt-1 text-gray-800">
                                            ${{
                                                dollarsForDisplay(
                                                    state.cartTotals.tax,
                                                )
                                            }}
                                        </dd>
                                    </div>

                                    <div class="mt-4 mb-2">
                                        <div
                                            class="flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-bold text-gray-500 text-xl"
                                            >
                                                Total ({{
                                                    `${state.cartQuantity} ${state.cartQuantity === 1 ? "Item" : "Items"}`
                                                }}):
                                            </dt>
                                            <dd
                                                class="mt-1 font-semibold text-xl text-gray-800"
                                            >
                                                ${{
                                                    dollarsForDisplay(
                                                        state.cartTotals.total,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            v-if="state.country.code !== 'US'"
                                            class="mt-2 text-right text-xs text-gray-500"
                                        >
                                            <span>* All prices in USD.</span>
                                        </div>
                                    </div>

                                    <button
                                        v-if="
                                            !state.shippingOptions ||
                                            !Object.keys(state.shippingOptions)
                                                .length
                                        "
                                        @click="shippingRegionModalOpen = true"
                                        type="button"
                                        name="estimate_shipping"
                                        class="w-full inline-flex justify-center px-6 py-2 my-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                    >
                                        <TruckIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Estimate Shipping
                                    </button>

                                    <ShippingRegionModal
                                        @closeModal="
                                            shippingRegionModalOpen = false
                                        "
                                        :countries="countries"
                                        :errors="errors"
                                        :initialCountry="state.country"
                                        :initialRegion="state.region"
                                        :open="shippingRegionModalOpen"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            class="order-1 lg:order-3 overflow-hidden bg-white shadow rounded-lg border border-gray-200 mt-2 mb-6 lg:mt-2"
                        >
                            <CouponCodeInput :errors="errors" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { inject, ref, onMounted } from "vue";
import type { AppEmitter } from "../../emitter";
import { ArrowSmRightIcon, TruckIcon } from "@heroicons/vue/solid";
import { useCart } from "../../functions/useCart";
import { useMoney } from "../../functions/useMoney";
import { usePaypal } from "../../functions/usePaypal";
import { useProductOption } from "../../functions/useProductOption";
import CartItemImage from "./CartItemImage.vue";
import CartItemQuantityButtons from "./CartItemQuantityButtons.vue";
import CartItemSubscriptionOptionButtons from "./CartItemSubscriptionOptionButtons.vue";
import DeleteCartItemButton from "./DeleteCartItemButton.vue";
import ShippingOptionButtons from "./ShippingOptionButtons.vue";
import ShippingRegionModal from "./ShippingRegionModal.vue";
import CartItemDiscounts from "./CartItemDiscounts.vue";
import CartItemTips from "./CartItemTips.vue";
import CouponCodeInput from "./CouponCodeInput.vue";
import PaypalCheckoutButton from "./PaypalCheckoutButton.vue";
import ApplePayButton from "./ApplePayButton.vue";
import CartRecommendedProducts from "./CartRecommendedProducts.vue";
import Tooltip from "../Tooltip.vue";

const props = defineProps<{
    countries?: any;
    initialCart?: any;
    initialCartQuantity?: number;
    initialCartTotals?: any;
    initialDiscounts?: any;
    initialTips?: any;
    initialShippingTime?: any;
    initialShippingOptions?: any;
    initialCountry?: any;
    initialRegion?: string;
    recommendedProducts?: any;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { dollarsForDisplay } = useMoney();
const {
    errors,
    state,
    cartIsEmpty,
    onCartItemError,
    onCartItemUpdated,
    onShippingOptionError,
    onShippingOptionUpdated,
    onShippingRegionUpdated,
    onShippingRegionError,
    onCouponApplied,
    onCouponError,
    onCouponRemoved,
    onPaypalTokenGenerated,
    onPaypalTokenError,
} = useCart();
const { forgetPaypalNonce } = usePaypal();
const { productPageHref } = useProductOption();

const shippingRegionModalOpen = ref(false);
const alternateCheckoutAvailable = ref(false);

Object.assign(state, {
    cart: props.initialCart,
    cartQuantity: props.initialCartQuantity,
    cartTotals: props.initialCartTotals,
    discounts: props.initialDiscounts,
    tips: props.initialTips,
    shippingTimeId: props.initialShippingTime
        ? props.initialShippingTime.id
        : null,
    shippingOptions: props.initialShippingOptions,
    country: props.initialCountry,
    region: props.initialRegion,
    paypalToken: null,
});

onMounted(() => {
    emitter.on("cartItemUpdated", (data) => onCartItemUpdated(data));
    emitter.on("cartItemError", (error) => onCartItemError(error));
    emitter.on("shippingOptionUpdated", (data) =>
        onShippingOptionUpdated(data),
    );
    emitter.on("shippingOptionError", (error) => onShippingOptionError(error));
    emitter.on("shippingRegionUpdated", (data) =>
        onShippingRegionUpdated(data),
    );
    emitter.on("shippingRegionError", (error) => onShippingRegionError(error));
    emitter.on("couponApplied", (data) => onCouponApplied(data));
    emitter.on("couponError", (error) => onCouponError(error));
    emitter.on("couponRemoved", (data) => onCouponRemoved(data));
    emitter.on("paypalTokenGenerated", (data) => onPaypalTokenGenerated(data));
    emitter.on("paypalTokenError", (data) => onPaypalTokenError(data));
    emitter.on(
        "alternateCheckoutAvailable",
        () => (alternateCheckoutAvailable.value = true),
    );
});
</script>
